import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { getUniqueKey } from '../../utils/HTMLutils';
import { ModalProps } from '../modal/Modal';
import { ModalMask } from '../modal/ModalMask';
import './../../hooks/useModalManager/useModalManager.scss';
import './../modal/Modal.scss';

const rootClassName = 'comp-modal-manager';
const maskRootClassName = 'component-modal-mask';

const hackFocus = () => {
  const tmp = document.createElement('input');
  document.body.appendChild(tmp);
  tmp.focus();
  document.body.removeChild(tmp);
};

export interface ModalManagerType {
  openModal: <C extends React.ElementType>(
    component: C,
    modalProps: React.ComponentProps<C>
  ) => string | number;
  closeModal: (modalKey?: string | number) => void;
}

export const ModalManager = forwardRef<ModalManagerType>((_props, ref) => {
  const [arrayOfModal, setArrayOfModal] = useState<
    { props?: ModalProps; component: React.ElementType }[]
  >([]);
  const refModals = useRef(arrayOfModal);
  refModals.current = arrayOfModal;

  useEffect(() => {
    const escapeCall = (evt: globalThis.KeyboardEvent) => {
      if (evt.key === 'Escape' && refModals.current.length) {
        const mprops = refModals.current[refModals.current.length - 1].props;
        if (!mprops?.preventEscExit) {
          closeModalByKey(mprops?.modalKey);
          if (typeof mprops?.onClose === 'function') {
            mprops?.onClose(mprops?.modalKey);
          }
        }
      }
    };
    document.addEventListener('keydown', escapeCall);
    return () => {
      document.removeEventListener('keydown', escapeCall);
    };
  }, []);

  const closeModalByKey = (modalKey?: string | number) => {
    let arr = refModals.current.slice();
    arr = arr.filter((obj) => String(obj.props?.modalKey) !== String(modalKey));
    setArrayOfModal(arr);
  };

  useImperativeHandle(ref, () => ({
    openModal<C extends React.ElementType>(
      component: C,
      modalProps: React.ComponentProps<C>
    ) {
      const modalKey = modalProps.modalKey
        ? modalProps.modalKey
        : getUniqueKey();
      if (typeof component === 'function') {
        const arr = refModals.current.slice();
        const p = {
          ...modalProps,
          modalKey,
          onClose: (modalKey?: string | number) => {
            closeModalByKey(modalKey);
            if (typeof modalProps.onClose === 'function') {
              modalProps.onClose(modalKey);
            }
          },
        };
        arr.push({ component, props: p });
        setArrayOfModal(arr);
        hackFocus();

        return modalKey;
      }
      return '';
    },
    closeModal(modalKey: string | number) {
      if (modalKey) {
        closeModalByKey(modalKey);
      } else if (refModals.current.length) {
        closeModalByKey(
          refModals.current[refModals.current.length - 1]?.props?.modalKey
        );
      }
    },
  }));

  return (
    <React.Fragment>
      <TransitionGroup className={`${rootClassName}-modals`}>
        {arrayOfModal.map((obj) => {
          const ModalComponent = React.createElement(obj.component, obj.props);
          return (
            <CSSTransition
              timeout={300}
              classNames={`${rootClassName}-mask`}
              key={obj.props?.modalKey}
              unmountOnExit
            >
              <ModalMask
                mobileOn={obj.props?.mobileOn}
                onClick={(evt) => {
                  if (
                    (evt.target as HTMLElement).classList.contains(
                      maskRootClassName
                    )
                  ) {
                    if (!obj.props?.preventMaskExit) {
                      obj.props?.onClose?.(obj.props?.modalKey);
                    }
                  }
                }}
              >
                {ModalComponent}
              </ModalMask>
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    </React.Fragment>
  );
});

ModalManager.displayName = 'ModalManager';
